/**
 *
 * SentimentAnalysis
 *
 */

import React, { Fragment } from "react"
import { connect } from "react-redux"
import { Helmet } from "react-helmet"
import { createStructuredSelector } from "reselect"
import { Link } from "react-router-dom"
import { compose } from "redux"
import injectSaga from "utils/injectSaga"
import injectReducer from "utils/injectReducer"
import { isEqual, get, isEmpty } from "lodash"
import SelectHR from "components/Select"

import {
  makeSelectWidgetsData,
  makeSelectIsLoadingQuestions,
  makeSelectQuestions,
  makeSelectTeams,
  makeSelectIsLoadingWidget,
  makeSelectDivisions,
  makeSelectDivisionsAvailable,
  makeSelectReportMeasureInfo,
} from "containers/Report/Home/selectors"

import { makeSelectCurrentMedition } from "containers/Report/Header/selectors"

import {
  makeSelectIsLoadingSentimentWordcloud,
  makeSelectSentimentWordcloud,
} from "containers/Reports/QualitativeQuestionList/selectors"

import {
  getQualitativeWatcher,
  refetchSentimentWordcloudWatcher,
  downloadSentimentWordcloudWatcher,
} from "containers/Report/Home/actions"
import reducer from "containers/Report/Home/reducer"
import saga from "containers/Report/Home/saga"
// El sentiment-wordcloud (bubble chart) se dispara desde containers/Report/Home/saga
// pero lo procesa el reducer de QualitativeQuestionList (mismo slice
// "qualitativeQuestionListInside" que usaba <QualitativeQuestionList>). Como esta
// vista ya no renderiza ese componente, hay que inyectar su reducer acá a mano o
// Redux ignora la acción de éxito silenciosamente (el selector cae siempre al
// initialState importado, con isLoadingSentimentWordcloud en true para siempre).
import qualitativeQuestionListReducer from "containers/Reports/QualitativeQuestionList/reducer"

import Layout from "components/Layout"
import { Select, Spin, Input, Popover, Button } from "antd"
import ReportHeader from "containers/Report/Header"
import AnswersList from "./AnswersList"
import EmptyStateAnonymity from "components/EmptyStateAnonymity"
import { WORD_CLOUD, DISABLE } from "utils/constants"
import iconPencil from "images/icon-pencil.svg"
import emptySentimentImage from "images/empty/sentiment-analysis.svg"
import BlockFreemiumPageBlur from "components/BlockFreemiumPageBlur"
import "./index.less"
import QualitativeSentimentBubbles, {
  wordsToTopics,
} from "components/QualitativeSentimentBubbles"
import {
  CloseOutlined,
  SearchOutlined,
  LeftOutlined,
  DownloadOutlined,
} from "@ant-design/icons"
import {
  isAmbassadorActive,
  isModeInModuleBySlug,
  LOCAL_ANONYMITY,
  LOCAL_ANONYMITY_LEADERS,
} from "../../../utils/storage"
import { validate } from "../../../utils/functions"
import { loadTexts } from "../../../utils/i18n"

const { Option } = Select

// Sentinel de UI para la opción "Todas las preguntas": el filtro real hacia
// AnswersList/sentiment-wordcloud sigue siendo questionSelected=null (ver
// AnswersList `"filter[question]": question || null`), pero antd Select
// necesita un value no-nulo para pintar la opción como seleccionada en vez
// de caer al placeholder.
const ALL_QUESTIONS_VALUE = "all"

/* eslint-disable react/prefer-stateless-function */
export class SentimentAnalysis extends React.Component {
  intlQualitativeQuestion = loadTexts("reports_qualitative_questions_view")
  state = {
    measure_id: this.props.match.params.id,
    questionSelected: null,
    divisionSelected: null,
    totalAnswers: undefined,
    textFilter: "",
    sentimentFilter: null,
  }

  componentDidMount() {
    const { location } = this.props
    const searchLocation = getUrlParams(location.search)

    if (searchLocation) {
      this.setState({
        divisionSelected: searchLocation.id_division || undefined,
      })
    }

    let paramPath = { ...searchLocation }

    const idSegment = !validate.exist(paramPath.id_segment)
      ? null
      : paramPath.id_segment

    this.props.getQualitative({
      isHome: true,
      measure: this.state.measure_id,
      history: this.props.history,
      division: !validate.exist(paramPath.id_division)
        ? null
        : paramPath.id_division,
      params: {
        id_division: !validate.exist(paramPath.id_division)
          ? null
          : paramPath.id_division,
        id_divisionParent: !validate.exist(paramPath.id_divisionParent)
          ? null
          : paramPath.id_divisionParent,
        id_segment: idSegment,
      },
      id_segment: idSegment,
      skipWordcloud: true,
      fetchSentimentWordcloud: true,
    })
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.questions, nextProps.questions)) {
      // Valor por defecto del selector = "Todas las preguntas" (FS-03 del PRD),
      // no la primera pregunta de la lista.
      this.setState({
        questionSelected: null,
      })
    }
  }

  // El bubble chart se pobló una vez en componentDidMount (getQualitative) —
  // si el usuario cambia de pregunta o división DESPUÉS, hay que refrescarlo
  // por separado (ya no pasa por QualitativeQuestionList). Ojo: componentDidMount
  // ya hace su propio setState (divisionSelected) antes de que measure/pregunta
  // terminen de cargar — eso dispara este componentDidUpdate prematuramente, con
  // params a medio cargar, y esa respuesta puede pisar en carrera al resultado
  // correcto del bootstrap. Por eso se gatea con hasBootstrapped: recién se
  // habilita el refetch una vez que el bootstrap (isLoadingWidget) terminó.
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.isLoadingWidget && !this.props.isLoadingWidget) {
      this.hasBootstrapped = true

      // Bloqueo de acceso directo por URL: si la medición no tiene sentiment
      // analysis habilitado, esta vista no debe poder verse aunque se entre
      // a la fuerza — se manda a la vista tradicional de preguntas cualitativas.
      if (!this.props.measureInfo.sentiment_analysis_version) {
        this.props.history.replace(
          `/reports/measure/${this.state.measure_id}/qualitative-question${this.props.location.search}`
        )
      }
      return
    }

    if (
      this.hasBootstrapped &&
      (prevState.questionSelected !== this.state.questionSelected ||
        prevState.divisionSelected !== this.state.divisionSelected)
    ) {
      this.props.refetchSentimentWordcloud({
        measure: this.state.measure_id,
        question: this.state.questionSelected,
        division: this.state.divisionSelected,
        id_survey_type: get(this.props.current_medition, "id_survey_type", 1),
        params: {
          id_division: this.state.divisionSelected,
        },
      })
    }
  }

  searchFilter = () => {}

  // Descarga el Excel de sentiment-wordcloud para lo que el usuario está
  // viendo ACTUALMENTE (pregunta/división seleccionadas en la UI), no lo que
  // haya en la URL — a diferencia de la vista vieja, que solo re-leía el URL.
  downloadQuestions = () => {
    this.props.downloadSentimentWordcloud({
      measure: this.state.measure_id,
      question: this.state.questionSelected,
      division: this.state.divisionSelected,
      id_survey_type: get(this.props.current_medition, "id_survey_type", 1),
      params: {
        id_division: this.state.divisionSelected,
      },
    })
  }

  renderSearch = () => {
    const { textFilter } = this.state
    return (
      <Input
        value={textFilter}
        placeholder={this.intlQualitativeQuestion.searchPlaceHolderSentiment}
        style={{ width: 359, height: 32 }}
        onChange={(e) => {
          this.setState(
            {
              textFilter: e.target.value,
            },
            () => {
              this.searchFilter()
            }
          )
        }}
        suffix={
          textFilter ? (
            <CloseOutlined
              className="collaborators-header-search-clear"
              onClick={() => {
                this.setState({ textFilter: "" }, () => this.searchFilter())
              }}
            />
          ) : (
            <SearchOutlined />
          )
        }
      />
    )
  }

  renderSentimentFilter = () => {
    const { sentimentFilter } = this.state
    return (
      <Select
        allowClear
        placeholder={this.intlQualitativeQuestion.selectSentimentPlaceHolder}
        value={sentimentFilter || undefined}
        style={{ width: 286, height: 32 }}
        onChange={(value) => {
          this.setState({ sentimentFilter: value || null })
        }}>
        <Option value="positive">{this.intlQualitativeQuestion.sentimentPositive}</Option>
        <Option value="neutral">{this.intlQualitativeQuestion.sentimentNeutral}</Option>
        <Option value="negative">{this.intlQualitativeQuestion.sentimentNegative}</Option>
      </Select>
    )
  }

  renderQuestionSelect = () => {
    const { questionSelected } = this.state
    const { questions } = this.props
    if (!questions || questions.length === 0) return null
    return (
      <Select
        value={questionSelected || ALL_QUESTIONS_VALUE}
        placeholder={this.intlQualitativeQuestion.question}
        onChange={(s) => {
          this.setState({
            questionSelected: s === ALL_QUESTIONS_VALUE ? null : s,
          })
        }}
        style={{ width: 368, height: 32 }}>
        <Option value={ALL_QUESTIONS_VALUE}>
          {this.intlQualitativeQuestion.allQuestions}
        </Option>
        {questions.map((f, i) => (
          <Option key={i} value={f.id_question}>
            {f.name}
          </Option>
        ))}
      </Select>
    )
  }

  renderDownloadButton = () => {
    return (
      <Popover
        className="custom-icon-info"
        placement="top"
        trigger="hover"
        content={
          <div>
            <p
              style={{
                textAlign: "justify",
                marginBottom: "0",
                fontSize: 12,
                color: "#4F4F4F",
              }}>
              Descarga el conteo de <br />
              palabras más utilizadas <br />
              de la nube de palabras.
            </p>
          </div>
        }>
        <Button
          type="primary"
          className="ReportsSentimentAnalysis-download"
          onClick={() => {
            this.downloadQuestions()
          }}
          style={{ width: 118, height: 32 }}
          icon={<DownloadOutlined />}>
          Descargar
        </Button>
      </Popover>
    )
  }

  renderFilter = () => {
    const { divisionSelected } = this.state
    const { current_medition, teams, divisions_available } = this.props
    const type_survey = get(current_medition, "id_survey_type", 1)

    const searchLocation = getUrlParams(location.search)
    let paramsPath = { ...searchLocation }

    let id_divisionParent =
      isEmpty(paramsPath.id_divisionParent) ||
      paramsPath.id_divisionParent === undefined ||
      paramsPath.id_divisionParent === "undefined"
        ? ""
        : `&id_divisionParent=${paramsPath.id_divisionParent}`

    if (isAmbassadorActive() && id_divisionParent == "") {
      id_divisionParent = `&id_divisionParent=${paramsPath.id_division}`
    }

    const idSegment = validate.empty(paramsPath.id_segment)
      ? ""
      : `&id_segment=${paramsPath.id_segment}`

    if (type_survey === 2) {
      return (
        <SelectHR
          allowClear
          value={divisionSelected || undefined}
          options={teams.filter((obj, pos, arr) => {
            return (
              arr
                .map((mapObj) => mapObj.id_position)
                .indexOf(obj.id_position) === pos
            )
          })}
          placeholder={this.intlQualitativeQuestion.selectLeaderPlaceHolder}
          style={{ width: 224 }}
          label="name"
          labelKey="id_employee"
          onChange={(s) => {
            s = s ? parseInt(s.id_position) : null
            this.setState({ divisionSelected: s })
          }}
        />
      )
    }

    return (
      <SelectHR
        allowClear={!validRole({ role: [5] })}
        value={divisionSelected || undefined}
        options={divisions_available}
        placeholder={this.intlQualitativeQuestion.selectDivisionPlaceHolder}
        style={{ width: 224 }}
        label="name"
        labelKey="id_division"
        onChange={(s) => {
          s = s ? parseInt(s.id_division) : null
          if (s) {
            this.props.history.push(
              `${this.props.history.location.pathname}?id_division=${s}${id_divisionParent}${idSegment}`
            )
          } else {
            this.props.history.push(
              `${this.props.history.location.pathname}?f${idSegment}`
            )
          }
          this.setState({ divisionSelected: s })
        }}
      />
    )
  }

  renderButtonBack = () => {
    const { measureInfo } = this.props
    // Function to return to medition o report home
    let link = `/reports/measure/${measureInfo.id_survey_type}/`
    if (this.props.isMeasure) {
      link = "/models/measure/"
    }
    return (
      <div className="ReportsDimensionByDivision-header-btn-back">
        <span>
          <Link
            to={link + this.props.match.params.id + this.props.location.search}>
            <LeftOutlined />{" "}
            {this.props.isMeasure
              ? "Medición"
              : this.intlQualitativeQuestion.linkReports}
          </Link>
        </span>
      </div>
    )
  }

  render() {
    const {
      questionSelected,
      divisionSelected,
      measure_id,
      totalAnswers,
      textFilter,
      sentimentFilter,
    } = this.state
    const {
      history,
      match,
      divisions,
      isLoadingWidget,
      current_medition,
      sentimentWordcloud,
      isLoadingSentimentWordcloud,
      measureInfo,
      divisions_available,
    } = this.props
    const divisionSelectedInfo = divisions_available.find(
      (d) => d.id_division === divisionSelected
    )
    const anonymityCount = !validRole({ role: [5, 6] })
      ? LOCAL_ANONYMITY()
      : LOCAL_ANONYMITY_LEADERS()
    const anonymityValidate =
      divisionSelectedInfo &&
      divisionSelectedInfo.employees < parseInt(anonymityCount)

    let isWordCloudModule = isModeInModuleBySlug(WORD_CLOUD)

    const nameSegment = !validate.empty(measureInfo.segment_name)
      ? `- ${measureInfo.segment_name}`
      : ""

    return (
      <Layout>
        <Helmet>
          <title>{this.intlQualitativeQuestion.titlePage}</title>
        </Helmet>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: this.props.isMeasure ? 88 : 0,
          }}>
          {!this.props.isMeasure && (
            <ReportHeader
              showDownload
              preSelectExport={[null, ["qualitative"]]}
              measure={measure_id}
              match={match}
              history={history}
              blankMeditionSelector
            />
          )}
          <div
            style={{ position: "relative" }}
            className="ReportsSentimentAnalysis">
            <div className="ReportsSentimentAnalysis-header">
              {this.renderButtonBack()}
            </div>

            <div className="ReportsSentimentAnalysis-title-row">
              <h4>
                <img
                  src={iconPencil}
                  alt="icon-pencil"
                  style={{ marginRight: 5 }}
                />
                {`${`${this.intlQualitativeQuestion.title} ${nameSegment}`}`}
              </h4>

              {!isLoadingWidget && (
                <div className="ReportsSentimentAnalysis-title-row-actions">
                  {this.renderQuestionSelect()}
                  {this.renderFilter()}
                  {this.renderDownloadButton()}
                </div>
              )}
            </div>

            {anonymityValidate ? (
              <EmptyStateAnonymity
                count={get(divisionSelectedInfo, "employees", 0)}
                image={emptySentimentImage}
                title={this.intlQualitativeQuestion.noResultsYet}
                description={null}
              />
            ) : isLoadingWidget ? (
              <div className="loadingSpin">
                <Spin />
              </div>
            ) : (
              <Fragment>
                {isWordCloudModule !== DISABLE && (
                  <div className="ReportsSentimentAnalysis-wordcloud">
                    <div className="ReportsSentimentAnalysis-wordcloud-content">
                      {!isLoadingSentimentWordcloud &&
                        Array.isArray(sentimentWordcloud) && (
                          <QualitativeSentimentBubbles
                            topics={wordsToTopics(sentimentWordcloud)}
                            height={340}
                          />
                        )}
                      {isLoadingSentimentWordcloud && (
                        <div className="ReportsSentimentAnalysis-loading-overlay">
                          <Spin size="large" />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="ReportsSentimentAnalysis-answers-header">
                  <span className="ReportsSentimentAnalysis-answers-header-title">
                    {`${this.intlQualitativeQuestion.answersTitle} (${totalAnswers || 0})`}
                  </span>
                  <div className="ReportsSentimentAnalysis-answers-header-filters">
                    {this.renderSearch()}
                    {this.renderSentimentFilter()}
                  </div>
                </div>

                <AnswersList
                  pagination
                  measure={measure_id}
                  division={divisionSelected}
                  isLeadership={get(current_medition, "id_survey_type", 1) === 2}
                  question={questionSelected}
                  sentimentFilter={sentimentFilter}
                  textFilter={textFilter}
                  onTotalChange={(t) => this.setState({ totalAnswers: t })}
                />
              </Fragment>
            )}
          </div>
        </div>

        <BlockFreemiumPageBlur
          view={`${
            get(current_medition, "id_survey_type", 1) === 1
              ? "engagement"
              : "leadership"
          }-qualitative-question`}
        />
      </Layout>
    )
  }
}

SentimentAnalysis.propTypes = {
  // dispatch: PropTypes.func.isRequired,
}

const mapStateToProps = createStructuredSelector({
  divisions: makeSelectDivisions(),
  divisions_available: makeSelectDivisionsAvailable(),
  teams: makeSelectTeams(),

  isLoadingWidget: makeSelectIsLoadingWidget(),
  widgets_data: makeSelectWidgetsData(),

  questions: makeSelectQuestions(),

  current_medition: makeSelectCurrentMedition(),

  sentimentWordcloud: makeSelectSentimentWordcloud(),
  isLoadingSentimentWordcloud: makeSelectIsLoadingSentimentWordcloud(),

  measureInfo: makeSelectReportMeasureInfo(),
})

function mapDispatchToProps(dispatch) {
  return {
    getQualitative: (params) => dispatch(getQualitativeWatcher(params)),
    refetchSentimentWordcloud: (params) =>
      dispatch(refetchSentimentWordcloudWatcher(params)),
    downloadSentimentWordcloud: (params) =>
      dispatch(downloadSentimentWordcloudWatcher(params)),
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)

const withReducer = injectReducer({ key: "reportHome", reducer })
const withSaga = injectSaga({ key: "reportHome", saga })
const withQualitativeQuestionListReducer = injectReducer({
  key: "qualitativeQuestionListInside",
  reducer: qualitativeQuestionListReducer,
})

export default compose(
  withReducer,
  withQualitativeQuestionListReducer,
  withSaga,
  withConnect
)(SentimentAnalysis)
